import React, { ReactElement, useContext, useMemo, useReducer, useRef } from "react";
import { CartReducer, CartState } from "@cart-client/reducers/CartReducer";
import { GiftingCartReducer, GiftingCartState } from "@cart-client/reducers/GiftingCartReducer";
import { MROCartReducer, MROCartState } from "@cart-client/reducers/MROCartReducer";
import { createQuickCheckoutCartReducer } from "@cart-client/reducers/QuickCheckoutCartReducer";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
import { randomStringIdGenerator, RouteContext, RouteContextValue } from "@swiggy-private/core-ui";
import { CheckoutRoute } from "@swiggy-private/checkout-core";
import { INTERNAL_CART_SOURCE } from "@cart-client/interfaces/Cart";

const initialState: CartState | GiftingCartState | MROCartState = {
    localCart: null,
    reviewedCart: null,
    uniqueIdForCart: randomStringIdGenerator(),
};

export function CartContextProvider(props: { children: React.ReactNode }): ReactElement {
    const { data } = useContext(CartFlowContext);
    const { route } = useContext(RouteContext) as RouteContextValue<CheckoutRoute>;

    /**
     * `cartSource` is frozen at mount, so the active route is the only signal tracking the visible
     * screen after the View Cart CTA. Kept in a ref because React 16 re-invokes a queued action
     * when the reducer identity changes, which would replay the native bridge write post-navigation.
     */
    const isQuickCheckoutScreen = route?.name === CheckoutRoute.QUICK_CHECKOUT;
    const skipNativeLocalCartSyncRef = useRef(isQuickCheckoutScreen);
    skipNativeLocalCartSyncRef.current = isQuickCheckoutScreen;

    const reducer = useMemo(() => {
        switch (data?.cartSource) {
            case INTERNAL_CART_SOURCE.GIFTING:
                return GiftingCartReducer;
            case INTERNAL_CART_SOURCE.QUICK_CHECKOUT:
                return createQuickCheckoutCartReducer({
                    shouldSkipNativeLocalCartSync: () => skipNativeLocalCartSyncRef.current,
                });
            case INTERNAL_CART_SOURCE.MRO_FOOD:
                return MROCartReducer;
            default:
                return CartReducer;
        }
    }, [data?.cartSource]);

    const [cartState, setCartState] = useReducer(reducer, initialState);

    const cartContextValue = React.useMemo(
        () => ({
            cartState,
            setCartState,
        }),
        [cartState, setCartState],
    );
    return <CartContext.Provider value={cartContextValue}>{props.children}</CartContext.Provider>;
}
